/**
 * App.tsx — the authenticated application surface.
 *
 * Renders inside <AuthGate> (MSAL guarantees an authenticated account by the
 * time this mounts). It builds the token-bearing API client and renders the
 * shell + the active panel. Navigation is hash-based to match the Sidebar's
 * `href="#<id>"` links (no router dependency added): the leading hash segment
 * selects the panel (`#health`, `#leads`, `#leads/<id>`, `#conversations`), so
 * the LS8 / CONV8 detail routes slot in under `#leads/<id>` / `#conversations/
 * <thread>` without restructuring. The CONV7 list reserves `#conversations`; the
 * CONV8 detail page slots under `#conversations/<thread>` later. `#numbers`
 * follows the same pattern: a second segment (`#numbers/products`) selects the
 * Products page, rendered by `NumbersPanel` — see `readRoute` below.
 *
 * The `BackofficeUser` *authorization* check is the BFF's; when an API call 401/403s,
 * the active page renders the clean UnauthorizedScreen instead of a broken page.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { AppShell } from './features/shell/AppShell';
import { HealthPage } from './features/health/HealthPage';
import { LeadsPage } from './features/leads/LeadsPage';
import { LeadDetailPage } from './features/leads/LeadDetailPage';
import { ConversationsWorkbench } from './features/conversations/ConversationsWorkbench';
import { ConfigPage } from './features/config/ConfigPage';
import { NumbersPanel } from './features/numbers/NumbersPanel';
import { createApiClient, type ApiClient } from './api/client';
import { useApiToken } from './auth/useApiToken';
import { UserMenu } from './auth/UserMenu';

type PanelId = 'health' | 'leads' | 'conversations' | 'config' | 'numbers';

/**
 * The active route, parsed from the hash. The leading segment selects the panel
 * (`#health`, `#leads`, `#conversations`, `#numbers`); a second segment under
 * `#leads/<id>` selects the LS8 detail view, under `#conversations/<thread>`
 * selects the CONV8 detail view, and under `#numbers/<tab>` selects the Numbers
 * sub-page (`products` → Products; anything else/empty → Number, the default).
 * Sidebar nav highlights the panel for both the list and its detail.
 */
interface Route {
  panel: PanelId;
  leadId: string | null;
  /** Decoded thread_id when on a conversation detail route; null for the list. */
  threadId: string | null;
  /** Numbers sub-tab; only meaningful when `panel === 'numbers'`. */
  numbersTab: 'number' | 'products';
}

const readRoute = (): Route => {
  const segments = window.location.hash.replace(/^#\/?/, '').split('/');
  const head = segments[0];
  if (head === 'conversations') {
    const thread = segments[1];
    return {
      panel: 'conversations',
      leadId: null,
      threadId: thread != null && thread !== '' ? decodeURIComponent(thread) : null,
      numbersTab: 'number',
    };
  }
  if (head === 'config')
    return { panel: 'config', leadId: null, threadId: null, numbersTab: 'number' };
  if (head === 'numbers') {
    return {
      panel: 'numbers',
      leadId: null,
      threadId: null,
      numbersTab: segments[1] === 'products' ? 'products' : 'number',
    };
  }
  if (head !== 'leads')
    return { panel: 'health', leadId: null, threadId: null, numbersTab: 'number' };
  const id = segments[1];
  return {
    panel: 'leads',
    leadId: id != null && id !== '' ? decodeURIComponent(id) : null,
    threadId: null,
    numbersTab: 'number',
  };
};

const useRoute = (): Route => {
  const [route, setRoute] = useState<Route>(readRoute);
  useEffect(() => {
    const onHashChange = () => setRoute(readRoute());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  return route;
};

/** Route + shell, given a ready client. Shared by `App` and the dev-only preview page. */
export const AppBody = ({
  apiClient,
  userMenu = undefined,
}: {
  apiClient: ApiClient;
  userMenu?: ReactNode;
}) => {
  const { panel, leadId, threadId, numbersTab } = useRoute();

  let content;
  if (panel === 'leads' && leadId != null) {
    // `key` remounts the detail page when navigating between two leads so the
    // fetch hook re-runs cleanly for the new id.
    content = <LeadDetailPage key={leadId} apiClient={apiClient} leadId={leadId} />;
  } else if (panel === 'leads') {
    content = <LeadsPage apiClient={apiClient} />;
  } else if (panel === 'conversations') {
    content = <ConversationsWorkbench apiClient={apiClient} threadId={threadId} />;
  } else if (panel === 'config') {
    content = <ConfigPage apiClient={apiClient} />;
  } else if (panel === 'numbers') {
    content = <NumbersPanel apiClient={apiClient} activeTab={numbersTab} />;
  } else {
    content = <HealthPage apiClient={apiClient} />;
  }

  return (
    <AppShell activeId={panel} userMenu={userMenu}>
      {content}
    </AppShell>
  );
};

export const App = () => {
  const getToken = useApiToken();
  // Memoised so the client identity is stable across renders (the pages' fetch
  // effects depend on it).
  const apiClient = useMemo(() => createApiClient(getToken), [getToken]);
  return <AppBody apiClient={apiClient} userMenu={<UserMenu />} />;
};

export default App;
